import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, Upload } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { authService } from '@/utils/auth';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminProducts = () => {
  const { t } = useTranslation();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [formData, setFormData] = useState({
    name: { ru: '', lv: '' },
    description: { ru: '', lv: '' },
    category: 'rings',
    price: 0,
    original_price: null,
    images: [],
    popularity: 0,
    sizes: [],
    in_stock: true
  });
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    fetchProducts();
  }, []);

  const fetchProducts = async () => {
    try {
      const response = await axios.get(`${API}/products`);
      setProducts(response.data);
    } catch (error) {
      console.error('Error fetching products:', error);
      toast.error('Ошибка загрузки товаров');
    } finally {
      setLoading(false);
    }
  };

  const handleUploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    const formDataUpload = new FormData();
    formDataUpload.append('file', file);

    try {
      const response = await axios.post(`${API}/admin/upload`, formDataUpload, {
        headers: {
          ...authService.getAuthHeader(),
          'Content-Type': 'multipart/form-data'
        }
      });
      
      setFormData(prev => ({
        ...prev,
        images: [...prev.images, response.data.url]
      }));
      toast.success('Изображение загружено');
    } catch (error) {
      console.error('Error uploading image:', error);
      toast.error('Ошибка загрузки изображения');
    } finally {
      setUploading(false);
    }
  };

  const handleCreate = () => {
    setSelectedProduct(null);
    setFormData({
      name: { ru: '', lv: '' },
      description: { ru: '', lv: '' },
      category: 'rings',
      price: 0,
      original_price: null,
      images: [],
      popularity: 0,
      sizes: [],
      in_stock: true
    });
    setEditDialogOpen(true);
  };

  const handleEdit = (product) => {
    setSelectedProduct(product);
    setFormData({
      name: product.name,
      description: product.description,
      category: product.category,
      price: product.price,
      original_price: product.original_price || null,
      images: product.images,
      popularity: product.popularity,
      sizes: product.sizes || [],
      in_stock: product.in_stock !== false
    });
    setEditDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      if (selectedProduct) {
        await axios.put(
          `${API}/admin/products/${selectedProduct.id}`,
          formData,
          { headers: authService.getAuthHeader() }
        );
        toast.success('Товар обновлён');
      } else {
        await axios.post(
          `${API}/admin/products`,
          formData,
          { headers: authService.getAuthHeader() }
        );
        toast.success('Товар создан');
      }
      setEditDialogOpen(false);
      fetchProducts();
    } catch (error) {
      console.error('Error saving product:', error);
      toast.error('Ошибка сохранения');
    }
  };

  const handleDelete = async () => {
    if (!selectedProduct) return;

    try {
      await axios.delete(`${API}/admin/products/${selectedProduct.id}`, {
        headers: authService.getAuthHeader()
      });
      toast.success('Товар удалён');
      setDeleteDialogOpen(false);
      fetchProducts();
    } catch (error) {
      console.error('Error deleting product:', error);
      toast.error('Ошибка удаления');
    }
  };

  const confirmDelete = (product) => {
    setSelectedProduct(product);
    setDeleteDialogOpen(true);
  };

  return (
    <div data-testid="admin-products-page">
      <div className="flex justify-between items-center mb-8">
        <h1 className="font-heading text-4xl tracking-tight">{t('admin.products')}</h1>
        <Button
          onClick={handleCreate}
          className="uppercase tracking-widest"
          data-testid="add-product-button"
        >
          <Plus className="w-4 h-4 mr-2" />
          {t('admin.addProduct')}
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12">Загрузка...</div>
      ) : (
        <div className="bg-background rounded-sm border border-border overflow-hidden">
          <table className="w-full" data-testid="products-table">
            <thead className="bg-muted">
              <tr>
                <th className="px-6 py-4 text-left font-body text-sm uppercase tracking-wide">Фото</th>
                <th className="px-6 py-4 text-left font-body text-sm uppercase tracking-wide">Название</th>
                <th className="px-6 py-4 text-left font-body text-sm uppercase tracking-wide">Категория</th>
                <th className="px-6 py-4 text-left font-body text-sm uppercase tracking-wide">Цена</th>
                <th className="px-6 py-4 text-left font-body text-sm uppercase tracking-wide">Статус</th>
                <th className="px-6 py-4 text-left font-body text-sm uppercase tracking-wide">Популярность</th>
                <th className="px-6 py-4 text-left font-body text-sm uppercase tracking-wide">{t('admin.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {products.map((product, index) => (
                <tr key={product.id} className="border-t border-border hover:bg-muted/50" data-testid={`product-row-${index}`}>
                  <td className="px-6 py-4">
                    <img src={product.images[0]} alt={product.name.ru} className="w-16 h-16 object-cover rounded" />
                  </td>
                  <td className="px-6 py-4">
                    <div>
                      <p className="font-medium">{product.name.ru}</p>
                      <p className="text-sm text-muted-foreground">{product.name.lv}</p>
                    </div>
                  </td>
                  <td className="px-6 py-4 uppercase">{product.category}</td>
                  <td className="px-6 py-4">€{product.price}</td>
                  <td className="px-6 py-4">
                    {product.in_stock !== false ? (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                        ✓ В наличии
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
                        ⏳ Под заказ
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4">{product.popularity}</td>
                  <td className="px-6 py-4">
                    <div className="flex space-x-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEdit(product)}
                        data-testid={`edit-button-${index}`}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => confirmDelete(product)}
                        data-testid={`delete-button-${index}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="edit-product-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading text-2xl tracking-tight">
              {selectedProduct ? t('admin.editProduct') : t('admin.addProduct')}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-6 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('admin.nameRu')}</Label>
                <Input
                  value={formData.name.ru}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: { ...prev.name, ru: e.target.value } }))}
                  data-testid="name-ru-input"
                />
              </div>
              <div className="space-y-2">
                <Label>{t('admin.nameLv')}</Label>
                <Input
                  value={formData.name.lv}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: { ...prev.name, lv: e.target.value } }))}
                  data-testid="name-lv-input"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>{t('admin.category')}</Label>
                <Select value={formData.category} onValueChange={(val) => setFormData(prev => ({ ...prev, category: val }))}>
                  <SelectTrigger data-testid="category-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rings">Rings / Кольца</SelectItem>
                    <SelectItem value="earrings">Earrings / Серьги</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>{t('admin.price')}</Label>
                <Input
                  type="number"
                  value={formData.price}
                  onChange={(e) => setFormData(prev => ({ ...prev, price: parseFloat(e.target.value) }))}
                  data-testid="price-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Старая цена (для акции) - необязательно</Label>
              <Input
                type="number"
                placeholder="Оставьте пустым, если нет скидки"
                value={formData.original_price || ''}
                onChange={(e) => setFormData(prev => ({ ...prev, original_price: e.target.value ? parseFloat(e.target.value) : null }))}
                data-testid="original-price-input"
              />
              <p className="text-xs text-muted-foreground">Если указана старая цена, на сайте будет показана акция со скидкой</p>
            </div>

            <div className="space-y-2">
              <Label>{t('admin.descRu')}</Label>
              <Textarea
                value={formData.description.ru}
                onChange={(e) => setFormData(prev => ({ ...prev, description: { ...prev.description, ru: e.target.value } }))}
                rows={3}
                data-testid="desc-ru-input"
              />
            </div>

            <div className="space-y-2">
              <Label>{t('admin.descLv')}</Label>
              <Textarea
                value={formData.description.lv}
                onChange={(e) => setFormData(prev => ({ ...prev, description: { ...prev.description, lv: e.target.value } }))}
                rows={3}
                data-testid="desc-lv-input"
              />
            </div>

            <div className="space-y-2">
              <Label>{t('admin.popularity')}</Label>
              <Input
                type="number"
                value={formData.popularity}
                onChange={(e) => setFormData(prev => ({ ...prev, popularity: parseInt(e.target.value) }))}
                data-testid="popularity-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Наличие товара</Label>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, in_stock: true }))}
                  className={`px-4 py-2 rounded-md border transition-colors ${
                    formData.in_stock 
                      ? 'bg-green-600 text-white border-green-600' 
                      : 'bg-background border-border hover:border-green-600'
                  }`}
                >
                  ✓ В наличии
                </button>
                <button
                  type="button"
                  onClick={() => setFormData(prev => ({ ...prev, in_stock: false }))}
                  className={`px-4 py-2 rounded-md border transition-colors ${
                    !formData.in_stock 
                      ? 'bg-amber-600 text-white border-amber-600' 
                      : 'bg-background border-border hover:border-amber-600'
                  }`}
                >
                  ⏳ Под заказ
                </button>
              </div>
            </div>

            <div className="space-y-2">
              <Label>Размеры кольца</Label>
              <div className="flex flex-wrap gap-2 mb-2">
                {formData.sizes.map((size, idx) => (
                  <span 
                    key={idx} 
                    className="inline-flex items-center gap-1 px-3 py-1 bg-accent/20 text-accent rounded-full text-sm"
                  >
                    {size}
                    <button
                      type="button"
                      onClick={() => setFormData(prev => ({ 
                        ...prev, 
                        sizes: prev.sizes.filter((_, i) => i !== idx) 
                      }))}
                      className="ml-1 hover:text-red-500"
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  id="size-input"
                  placeholder="Введите размер (например: 17)"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ',') {
                      e.preventDefault();
                      const value = e.target.value.trim();
                      if (value && !formData.sizes.includes(value)) {
                        setFormData(prev => ({ ...prev, sizes: [...prev.sizes, value] }));
                        e.target.value = '';
                      }
                    }
                  }}
                  data-testid="sizes-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    const input = document.getElementById('size-input');
                    const value = input.value.trim();
                    if (value && !formData.sizes.includes(value)) {
                      setFormData(prev => ({ ...prev, sizes: [...prev.sizes, value] }));
                      input.value = '';
                    }
                  }}
                >
                  Добавить
                </Button>
              </div>
              <p className="text-xs text-muted-foreground">Введите размер и нажмите Enter или кнопку "Добавить"</p>
            </div>

            <div className="space-y-2">
              <Label>{t('admin.images')}</Label>
              <div className="flex flex-wrap gap-2 mb-2">
                {formData.images.map((img, idx) => (
                  <div key={idx} className="relative w-20 h-20">
                    <img src={img} alt={`Product ${idx}`} className="w-full h-full object-cover rounded" />
                    <button
                      onClick={() => setFormData(prev => ({ ...prev, images: prev.images.filter((_, i) => i !== idx) }))}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex items-center gap-2">
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleUploadImage}
                  disabled={uploading}
                  className="hidden"
                  id="image-upload"
                />
                <Label htmlFor="image-upload">
                  <Button type="button" variant="outline" disabled={uploading} asChild>
                    <span>
                      <Upload className="w-4 h-4 mr-2" />
                      {uploading ? 'Загрузка...' : t('admin.uploadImage')}
                    </span>
                  </Button>
                </Label>
              </div>
            </div>

            <div className="flex justify-end space-x-2 pt-4">
              <Button variant="outline" onClick={() => setEditDialogOpen(false)} data-testid="cancel-button">
                {t('admin.cancel')}
              </Button>
              <Button onClick={handleSave} data-testid="save-button">
                {t('admin.save')}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('admin.deleteProduct')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('admin.confirmDelete')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('admin.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              {t('admin.delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
